import { runCommand, toolCommand, type CommandId } from '../commands';
import type { ViewerShell } from '../shell';
import { closeMenus, isMenuOpen } from './contextmenu';
import { isDialogOpen } from './dialogs';

/** Keyboard map of spec section 8. Keys are ignored while typing or while a dialog is open. */

const PLAIN_KEYS: Record<string, CommandId> = {
  arrowright: 'slice-next',
  arrowup: 'slice-next',
  u: 'slice-next',
  arrowleft: 'slice-prev',
  arrowdown: 'slice-prev',
  y: 'slice-prev',
  p: toolCommand('pan'),
  z: toolCommand('zoom'),
  w: toolCommand('window-level'),
  s: toolCommand('stack'),
  m: toolCommand('magnify'),
  a: toolCommand('angles'),
  l: toolCommand('lines'),
  b: toolCommand('ellipticalRoi'),
  c: toolCommand('cobbs'),
  d: toolCommand('probe'),
  e: toolCommand('erase'),
  t: toolCommand('triangulation'),
  r: 'reset',
  h: 'toggle-overlay',
  i: 'invert',
  f: 'cine-toggle',
  q: 'clear-slice',
  x: 'series-prev',
  v: 'series-next',
};

const CTRL_KEYS: Record<string, CommandId> = {
  arrowleft: 'align:Left',
  arrowright: 'align:Right',
  arrowup: 'align:Top',
  arrowdown: 'align:Bottom',
  c: 'copy-grid',
};

/** Digit → preset index: 1 = image default (0), 2..9 = presets 1..8, 0 = preset 9. */
function presetForDigit(key: string): CommandId | undefined {
  if (!/^[0-9]$/.test(key)) return undefined;
  const digit = Number(key);
  return `preset:${digit === 0 ? 9 : digit - 1}`;
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement) return !['checkbox', 'radio', 'button'].includes(target.type);
  return false;
}

/** Resolves a keydown to a command, or undefined when the key is not mapped. */
export function commandForKey(event: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>): CommandId | undefined {
  if (event.altKey) return undefined;
  const key = event.key.toLowerCase();
  if (event.ctrlKey || event.metaKey) {
    if (key === 'c') return CTRL_KEYS.c;
    return event.ctrlKey ? CTRL_KEYS[key] : undefined;
  }
  return PLAIN_KEYS[key] ?? presetForDigit(key);
}

export function install(shell: ViewerShell): void {
  void shell;
  document.addEventListener('keydown', (event) => {
    if (event.defaultPrevented || isDialogOpen() || isTyping(event.target)) return;
    if (event.key === 'Escape') {
      if (isMenuOpen()) closeMenus();
      return;
    }
    const command = commandForKey(event);
    if (!command) return;
    // Let the browser copy a real text selection.
    if (command === 'copy-grid' && (window.getSelection()?.toString() ?? '') !== '') return;
    event.preventDefault();
    if (isMenuOpen()) closeMenus();
    runCommand(command);
  });
}
